import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomerSelector from '../Common/CustomerSelector';
import SiteSelector from '../Common/SiteSelector';
import { CustomerDTO, UserRole, SiteDTO } from '@beable/roster-backend-client-v1';
import { PermissionCode } from '@beable/roster-backend-client-v1';
import { StyledPanelHeader } from '../Common/Layout/PanelHeader';
import { BasePanelBox } from '../Common/Layout/BasePanelBox';
import BasePanel from '../Common/Layout/BasePanel';
import PermissionSelector from './PermissionSelector';
import RoleSelector from './RoleSelector';
import { getStyles } from './styles';

// CustomerSelector/SiteSelector/PermissionSelector/RoleSelector only accept a
// `className` string prop (no `sx` support), so the shared style is applied via
// `styled(...)`, sourcing its values from getStyles instead of the old makeStyles hook.
const StyledCustomerSelector = styled(CustomerSelector)(({ theme }) => getStyles(theme).provisionSelector);
const StyledSiteSelector = styled(SiteSelector)(({ theme }) => getStyles(theme).provisionSelector);
const StyledPermissionSelector = styled(PermissionSelector)(({ theme }) => getStyles(theme).provisionSelector);
const StyledRoleSelector = styled(RoleSelector)(({ theme }) => getStyles(theme).provisionSelector);
interface PermissionPanelProps {
    onSiteSelect: (site: SiteDTO) => void;
    onPermissionSelect: (permission: PermissionCode) => void;
    onRoleSelect: (role: UserRole) => void;
}
export interface SelectablePermission {
  /**
   * Beable PermissionCode
   */
  code: PermissionCode;
  /**
   * Human-readable label
   */
  name: string;
  /**
   * Restrict to selection to defined rules.
   * By default no role is restricted
   */
  roles?: UserRole[];
}
export default function PermissionsPanel({ onSiteSelect, onPermissionSelect, onRoleSelect }: PermissionPanelProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | undefined>(undefined);
  const [limitedRoles, setLimitedRoles] = useState<UserRole[]>([]);
  const allRoles = Object.values(UserRole);
  // This can be changed to add any permission which we want to open up in admin webapp.
  // Does not currently support inherited permissions above site level.
  const permissions: SelectablePermission[] = useMemo(() => (
    [
      {
        code: PermissionCode.AdultEd,
        name: "Adult Education",
        roles: allRoles
      },
      {
        code: PermissionCode.AdultEdShortSurvey,
        name: "Adult Education Short Survey",
        roles: allRoles
      },
      {
        code: PermissionCode.AdminAdHocRosterAccess,
        name: "Class Creator",
        roles: [UserRole.teacher, UserRole.administrator]
      },
      {
        code: PermissionCode.DisableAIWriteReview,
        name: "Disable AI for Write Review",
        roles: allRoles
      },
      {
        code: PermissionCode.QuestionHelpDisableCrystalBall,
        name: "Disable Question Help: Crystal Ball",
        roles: allRoles
      },
      {
        code: PermissionCode.QuestionHelpDisableFiftyFifty,
        name: "Disable Question Help: 50:50",
        roles: allRoles
      },
      {
        code: PermissionCode.QuestionHelpDisableHint,
        name: "Disable Question Help: Hint",
        roles: allRoles
      },
      {
        code: PermissionCode.Xello,
        name: "Enable Xello Integration",
        roles: allRoles
      },
      {
        code: PermissionCode.SuppressOnboardingFlow,
        name: "Hide Onboarding Card",
        roles: allRoles
      },
      {
        code: PermissionCode.UserPilot,
        name: "Identify user with UserPilot",
        roles: allRoles
      },
      {
        code: PermissionCode.ReadOnlyLanguageSetting,
        name: "Prohibit language setting changes",
        roles: [UserRole.teacher]
      },
      {
        code: PermissionCode.SuppressContests,
        name: "Suppress Contests",
        roles: allRoles
      },
      {
        code: PermissionCode.SuppressWriteSection,
        name: "Suppress Write Section",
        roles: allRoles
      },
      {
        code: PermissionCode.TrackActivitiesByClass,
        name: "Track activities by class",
        roles: allRoles
      },
      {
        code: PermissionCode.WordStudyCourseCompletionOptional,
        name: "Word Study Intro Course Optional",
        roles: [UserRole.student]
      }
    ]
  ), []);
  
  const handleCustomerChange = (customer: CustomerDTO) => {
    setSelectedCustomer(customer);
  };
  const decoratedOnPermissionSelect = (permission: PermissionCode) => {
    const { roles = [] } = permissions.find(p => p.code === permission) || {};
    setLimitedRoles(roles);
    onPermissionSelect(permission);
  }

  return (
    <BasePanel>
      <BasePanelBox>
        <StyledPanelHeader header='Customer Permissions' />
        <Box sx={{
          p: 4
        }}>
          <StyledCustomerSelector
            onSelect={handleCustomerChange}
            variant='outlined'
          />
          <StyledSiteSelector
            siteId={undefined}
            onSelect={onSiteSelect}
            customerId={selectedCustomer ? selectedCustomer.id : ''}
            variant='outlined'
          />
          <StyledPermissionSelector
            onSelect={decoratedOnPermissionSelect}
            variant='outlined'
            permissions={permissions}
          />
          <StyledRoleSelector
            onSelect={onRoleSelect}
            variant='outlined'
            restrictTo={limitedRoles}
          />
        </Box>
      </BasePanelBox>
    </BasePanel>
  );
}